import { LinkProps, useNavigate } from "@tanstack/react-router"
import { Badge } from "design-system/atoms/Badge"
import { Button } from "design-system/atoms/Button"
import { Sheet, SheetContent } from "design-system/atoms/Sheet"
import { ComparisonActivityChart } from "design-system/organisms/insights/ComparisonActivityChart"
import { MetricCard } from "design-system/organisms/insights/MetricCard"
import { SentimentActivityChart } from "design-system/organisms/insights/SentimentActivityChart"
import { SimpleActivityChart } from "design-system/organisms/insights/SimpleActivityChart"
import { cn } from "design-system/utils"
import { TFunction } from "i18next"
import { formatPercent, MINUTES_SAVED_PER_QUESTION } from "lopermut"
import { ArrowRight, Flame, MoveRight, Sparkle } from "lucide-react"
import { useCanonicalTopicActivityQuery } from "modules/dashboard/hooks/useCanonicalTopicActivityQuery"
import { formatCanonicalTopicLabel } from "modules/shared/lib/formatCanonicalTopicLabel"
import { getSentimentColor, getSentimentLabel } from "modules/shared/lib/sentiment"
import { useMemo, useState } from "react"
import { useTranslation } from "react-i18next"
import {
  CanonicalTopicFilterBy,
  ClusterDomain,
  ClusterDomainEnum,
  GoalType,
  GoalTypeEnum,
} from "schema"
import { TopicItemsTable } from "./TopicItemsTable"
import { TooltipData } from "./TooltipOverlay"
import { TreemapsFilters } from "./TreemapsFilters"

type GroupBy = "day" | "week"

type Props = {
  domain: ClusterDomain
  goalType?: GoalType
  goalLabel?: string
  tooltip: TooltipData
  navigateTo: LinkProps | undefined
  onClose: () => void
  filterBy?: CanonicalTopicFilterBy
  onFiltersChange?: (partial: { filterBy?: CanonicalTopicFilterBy }) => void
}

export const TopicSidePanel = ({
  domain,
  goalType,
  goalLabel,
  tooltip,
  navigateTo,
  onClose,
  filterBy,
  onFiltersChange,
}: Props) => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const [groupBy, setGroupBy] = useState<GroupBy>("week")
  const canonicalTopicId = tooltip.node.id?.toString() ?? ""
  const hasActivityChart =
    domain === ClusterDomainEnum.Support ||
    domain === ClusterDomainEnum.VoiceOfCustomer ||
    domain === ClusterDomainEnum.Goal ||
    domain === ClusterDomainEnum.Question ||
    domain === ClusterDomainEnum.Issue ||
    domain === ClusterDomainEnum.ActionRequest

  const { data: activityData, isPending } = useCanonicalTopicActivityQuery({
    canonicalTopicId,
    domain,
    groupBy,
    filterBy,
    enabled: !!canonicalTopicId && hasActivityChart,
  })

  // Hours saved metrics (Support domain only)
  const hoursSavedMetrics = useMemo(() => {
    if (
      domain !== ClusterDomainEnum.Support ||
      !activityData ||
      activityData.domain !== ClusterDomainEnum.Support
    ) {
      return null
    }
    const hoursSaved = Math.floor(
      (activityData.allTimeSolvedCount * MINUTES_SAVED_PER_QUESTION) / 60,
    )
    const potentialHoursSaved = Math.floor(
      (activityData.allTimePotentialSolvedCount * MINUTES_SAVED_PER_QUESTION) / 60,
    )
    if (hoursSaved === 0 && potentialHoursSaved <= 10) return null
    return { hoursSaved, potentialHoursSaved }
  }, [domain, activityData])

  const deflectionRateMetrics = useMemo(() => {
    if (
      domain !== ClusterDomainEnum.Support ||
      !activityData ||
      activityData.domain !== ClusterDomainEnum.Support ||
      activityData.allTimeConversationsCount === 0
    ) {
      return null
    }
    return {
      count: activityData.allTimeTicketConversationsCount,
      rate: Math.round(
        (activityData.allTimeTicketConversationsCount / activityData.allTimeConversationsCount) *
          100,
      ),
    }
  }, [domain, activityData])

  const vocMetrics = useMemo(() => {
    if (
      domain !== ClusterDomainEnum.VoiceOfCustomer ||
      !activityData ||
      activityData.domain !== ClusterDomainEnum.VoiceOfCustomer
    ) {
      return null
    }
    return {
      allTimeInsightsCount: activityData.allTimeInsightsCount,
      allTimeAvgSentiment: activityData.allTimeAvgSentiment,
    }
  }, [domain, activityData])

  const questionMetrics = useMemo(() => {
    if (
      domain !== ClusterDomainEnum.Question ||
      !activityData ||
      activityData.domain !== ClusterDomainEnum.Question
    ) {
      return null
    }

    return {
      allTimeCount: activityData.allTimeCount,
      answeredCount: activityData.allTimeAnsweredCount,
      answeredRate:
        activityData.allTimeCount > 0
          ? Math.round((activityData.allTimeAnsweredCount / activityData.allTimeCount) * 100)
          : null,
      knowledgeGapCount: activityData.allTimeKnowledgeGapCount,
    }
  }, [domain, activityData])

  const issueMetrics = useMemo(() => {
    if (
      domain !== ClusterDomainEnum.Issue ||
      !activityData ||
      activityData.domain !== ClusterDomainEnum.Issue
    ) {
      return null
    }

    return {
      allTimeCount: activityData.allTimeCount,
      highUrgencyCount: activityData.allTimeHighUrgencyCount,
      highUrgencyRate:
        activityData.allTimeCount > 0
          ? Math.round((activityData.allTimeHighUrgencyCount / activityData.allTimeCount) * 100)
          : null,
    }
  }, [domain, activityData])

  const actionRequestMetrics = useMemo(() => {
    if (
      domain !== ClusterDomainEnum.ActionRequest ||
      !activityData ||
      activityData.domain !== ClusterDomainEnum.ActionRequest
    ) {
      return null
    }

    return {
      allTimeCount: activityData.allTimeCount,
      fulfilledCount: activityData.allTimeFulfilledCount,
      fulfillmentRate:
        activityData.allTimeCount > 0
          ? Math.round((activityData.allTimeFulfilledCount / activityData.allTimeCount) * 100)
          : null,
      eligibilityGapCount: activityData.allTimeEligibilityGapCount,
    }
  }, [domain, activityData])

  const groupByFilter = (
    <div className="self-end shrink-0">
      <div className="inline-flex rounded-md border border-input bg-background overflow-hidden divide-x divide-input w-fit">
        {(["day", "week"] as const).map((opt) => (
          <button
            key={opt}
            type="button"
            onClick={() => setGroupBy(opt)}
            className={cn(
              "px-3 py-1.5 text-xs whitespace-nowrap transition-colors focus-visible:outline-hidden focus-visible:ring focus-visible:ring-ring",
              groupBy === opt
                ? "bg-muted text-foreground font-medium"
                : "text-muted-foreground hover:bg-muted/40",
            )}
          >
            {t(
              opt === "day"
                ? "dashboard.tooltip.activity.daily"
                : "dashboard.tooltip.activity.weekly",
            )}
          </button>
        ))}
      </div>
    </div>
  )

  const activityChart = (() => {
    if (!activityData && !isPending) return null

    if (domain === ClusterDomainEnum.Support) {
      const dailyCount =
        activityData?.domain === ClusterDomainEnum.Support ? activityData.dailyCount : undefined

      return (
        <ComparisonActivityChart
          data={dailyCount?.map((d) => ({
            date: d.date,
            questions: d.baseValue,
            potential: d.extraValue,
            solved: d.topValue,
            solveRate: toPercent(d.topValue, d.baseValue),
            ticketRate: d.rate ? Math.round(d.rate * 100) : d.rate,
          }))}
          chartConfig={{
            questions: {
              label: t("dashboard.tooltip.activity.questions"),
              color: "var(--support-secondary)",
              type: "bar",
            },
            potential: {
              label: t("dashboard.tooltip.activity.potentialAutoSolved"),
              color: "color-mix(in srgb, var(--support-primary) 60%, transparent)",
              type: "bar",
            },
            solved: {
              label: t("dashboard.tooltip.activity.autoSolved"),
              color: "var(--support-primary)",
              type: "bar",
            },
            solveRate: {
              label: t("dashboard.support.solveRate"),
              color: "var(--chart-2)",
              type: "line",
            },
            ticketRate: {
              label: t("dashboard.metrics.ticketRate.title"),
              color: "var(--testing-2)",
              type: "line",
            },
          }}
          isPending={isPending}
          legendTooltipByKey={{
            potential: t("dashboard.tooltip.activity.potentialAutoSolvedLegendTooltip"),
            ticketRate: t("dashboard.tooltip.activity.ticketRateLegendTooltip"),
          }}
          action={groupByFilter}
        />
      )
    }

    if (domain === ClusterDomainEnum.Question) {
      const dailyCount =
        activityData?.domain === ClusterDomainEnum.Question ? activityData.dailyCount : undefined

      return (
        <ComparisonActivityChart
          data={dailyCount?.map((d) => ({
            date: d.date,
            questions: d.baseValue,
            potential: d.extraValue,
            answered: d.topValue,
            autoAnswerRate: toPercent(d.topValue, d.baseValue),
          }))}
          chartConfig={{
            questions: {
              label: t("dashboard.tooltip.activity.questions"),
              color: "color-mix(in srgb, var(--chart-4) 25%, transparent)",
              type: "bar",
            },
            potential: {
              label: t("dashboard.tooltip.activity.potentialAutoSolved"),
              color: "color-mix(in srgb, var(--chart-4) 60%, transparent)",
              type: "bar",
            },
            answered: {
              label: t("dashboard.tooltip.activity.autoAnswered"),
              color: "var(--chart-4)",
              type: "bar",
            },
            autoAnswerRate: {
              label: t("dashboard.tooltip.activity.autoAnswerRate"),
              color: "var(--chart-1)",
              type: "line",
            },
          }}
          isPending={isPending}
          legendTooltipByKey={{
            potential: t("dashboard.tooltip.activity.potentialAutoSolvedLegendTooltip"),
          }}
          action={groupByFilter}
        />
      )
    }

    if (domain === ClusterDomainEnum.Issue) {
      const dailyCount =
        activityData?.domain === ClusterDomainEnum.Issue ? activityData.dailyCount : undefined

      return (
        <ComparisonActivityChart
          data={dailyCount?.map((d) => ({
            date: d.date,
            lowUrgency: d.baseValue,
            mediumUrgency: d.extraValue,
            highUrgency: d.topValue,
          }))}
          chartConfig={{
            lowUrgency: {
              label: t("dashboard.tooltip.activity.lowUrgency"),
              color: "var(--neutral)",
              type: "bar",
            },
            mediumUrgency: {
              label: t("dashboard.tooltip.activity.mediumUrgency"),
              color: "var(--support-secondary)",
              type: "bar",
            },
            highUrgency: {
              label: t("dashboard.tooltip.activity.highUrgency"),
              color: "var(--support-primary)",
              type: "bar",
            },
          }}
          isPending={isPending}
          stackValueMode="segments"
          legendTooltip={t("dashboard.tooltip.activity.issueUrgencyLegendTooltip")}
          action={groupByFilter}
        />
      )
    }

    if (domain === ClusterDomainEnum.ActionRequest) {
      const dailyCount =
        activityData?.domain === ClusterDomainEnum.ActionRequest
          ? activityData.dailyCount
          : undefined
      return (
        <ComparisonActivityChart
          data={dailyCount?.map((d) => ({
            date: d.date,
            requests: d.baseValue,
            eligibilityGap: d.extraValue,
            fulfilled: d.topValue,
            fulfillmentRate: toPercent(d.topValue, d.baseValue),
          }))}
          chartConfig={{
            requests: {
              label: t("dashboard.tooltip.activity.requests"),
              color: "var(--chart-2)",
              type: "bar",
            },
            eligibilityGap: {
              label: t("dashboard.tooltip.activity.eligibilityGap"),
              color: "color-mix(in srgb, var(--chart-3) 60%, transparent)",
              type: "bar",
            },
            fulfilled: {
              label: t("dashboard.tooltip.activity.fulfilled"),
              color: "var(--chart-3)",
              type: "bar",
            },
            fulfillmentRate: {
              label: t("dashboard.tooltip.activity.fulfillmentRate"),
              color: "var(--chart-1)",
              type: "line",
            },
          }}
          isPending={isPending}
          legendTooltipByKey={{
            eligibilityGap: t("dashboard.tooltip.activity.eligibilityGapLegendTooltip"),
          }}
          action={groupByFilter}
        />
      )
    }

    if (domain === ClusterDomainEnum.VoiceOfCustomer) {
      const dailyCount =
        activityData?.domain === ClusterDomainEnum.VoiceOfCustomer
          ? activityData.dailyCount
          : undefined

      if (goalType === GoalTypeEnum.Classification) {
        return (
          <SimpleActivityChart
            data={dailyCount?.map((d) => ({ date: d.date, baseValue: d.totalCount }))}
            chartConfig={{
              baseValue: {
                label: t("dashboard.tooltip.activity.responses"),
                color: "var(--chart-4)",
              },
            }}
            isPending={isPending}
            action={groupByFilter}
          />
        )
      }

      return (
        <SentimentActivityChart
          data={dailyCount}
          chartConfig={{
            veryNegative: { label: t("shared.sentiments.veryNegative") },
            negative: { label: t("shared.sentiments.negative") },
            neutral: { label: t("shared.sentiments.neutral") },
            positive: { label: t("shared.sentiments.positive") },
            veryPositive: { label: t("shared.sentiments.veryPositive") },
            average: { label: t("dashboard.tooltip.activity.avgSentiment") },
            sentimentScale: { label: t("dashboard.tooltip.activity.sentimentScale") },
            totalResponses: { label: t("dashboard.tooltip.activity.responses") },
          }}
          isPending={isPending}
          action={groupByFilter}
        />
      )
    }

    if (domain === ClusterDomainEnum.Goal) {
      const dailyCount =
        activityData?.domain === ClusterDomainEnum.Goal ? activityData.dailyCount : undefined

      return (
        <SentimentActivityChart
          data={dailyCount}
          chartConfig={{
            veryNegative: { label: t("shared.sentiments.veryNegative") },
            negative: { label: t("shared.sentiments.negative") },
            neutral: { label: t("shared.sentiments.neutral") },
            positive: { label: t("shared.sentiments.positive") },
            veryPositive: { label: t("shared.sentiments.veryPositive") },
            average: { label: t("dashboard.tooltip.activity.avgSentiment") },
            sentimentScale: { label: t("dashboard.tooltip.activity.sentimentScale") },
            totalResponses: { label: t("dashboard.tooltip.activity.responses") },
          }}
          isPending={isPending}
          action={groupByFilter}
        />
      )
    }

    return null
  })()

  return (
    <Sheet
      open
      onSheetOpenChange={(open) => {
        if (!open) onClose()
      }}
    >
      <SheetContent
        side="right"
        className="w-full sm:w-[760px] sm:max-w-[760px] flex flex-col gap-0 p-0"
        onPointerDownOutside={(e) => {
          if ((e.target as Element).closest("svg")) e.preventDefault()
        }}
      >
        <div className="flex flex-col gap-6 p-6 overflow-y-auto">
          <div className="flex flex-col gap-4">
            <div className="text-xs font-medium uppercase text-muted-foreground">
              {goalLabel ?? t(`dashboard.domains.${domain}`)}
            </div>
            <div className="flex flex-row justify-between items-center gap-8">
              <div className="flex flex-row gap-4">
                <div className="text-xl font-semibold text-foreground leading-snug">
                  {formatCanonicalTopicLabel(tooltip.node.name)}
                </div>
                {(tooltip.node.streakInWeeks > 1 || tooltip.node.isNewTopic) && (
                  <div className="flex flex-row gap-2 items-center flex-wrap">
                    {tooltip.node.streakInWeeks > 1 && (
                      <Badge
                        variant="testing"
                        className="flex flex-row gap-1 items-center text-xs whitespace-nowrap"
                      >
                        <Flame size={14} className="text-testing-1" />
                        <span className="text-gradient-testing whitespace-nowrap">
                          {t("dashboard.tooltip.streakInWeeks", {
                            count: tooltip.node.streakInWeeks,
                          })}
                        </span>
                      </Badge>
                    )}
                    {tooltip.node.isNewTopic && (
                      <Badge
                        variant="gradient"
                        className="flex flex-row gap-1 items-center text-xs whitespace-nowrap"
                      >
                        <Sparkle size={14} className="text-chart-1" />
                        <span className="text-gradient whitespace-nowrap">
                          {t("dashboard.tooltip.newTopic")}
                        </span>
                      </Badge>
                    )}
                  </div>
                )}
              </div>
              {onFiltersChange && (
                <TreemapsFilters
                  filterBy={filterBy}
                  onChange={(newFilterBy) => onFiltersChange({ filterBy: newFilterBy })}
                />
              )}
            </div>
            {tooltip.node.description && (
              <p className="text-sm text-muted-foreground leading-snug line-clamp-4">
                {tooltip.node.description}
              </p>
            )}
          </div>

          {/* Support metrics */}
          {hoursSavedMetrics && (
            <div className="flex flex-row gap-4 items-stretch">
              <MetricCard
                title={t("dashboard.metrics.timeSaved.title")}
                tooltip={t("dashboard.metrics.timeSaved.tooltip")}
                currentValue={`${hoursSavedMetrics.hoursSaved}h`}
                metric={
                  <div
                    className={cn(
                      "flex flex-row items-center gap-1",
                      hoursSavedMetrics.potentialHoursSaved >= 10 &&
                        hoursSavedMetrics.potentialHoursSaved > 1.2 * hoursSavedMetrics.hoursSaved
                        ? "text-support-primary"
                        : "text-muted-foreground",
                    )}
                  >
                    <MoveRight className="size-4" />
                    <span>
                      {t("dashboard.metrics.timeSaved.potentialMetric", {
                        hours: hoursSavedMetrics.potentialHoursSaved,
                      })}
                    </span>
                  </div>
                }
                isPending={isPending}
              />
              {deflectionRateMetrics && (
                <MetricCard
                  title={t("dashboard.metrics.ticketRate.title")}
                  tooltip={t("dashboard.metrics.ticketRate.tooltip")}
                  currentValue={formatPercent({ amount: deflectionRateMetrics.rate, fix: 0 })}
                  metric={t("dashboard.metrics.ticketRate.metric", {
                    count: deflectionRateMetrics.count,
                  })}
                  isPending={isPending}
                />
              )}
            </div>
          )}

          {/* VoC metrics */}
          {vocMetrics && (
            <div className="flex flex-row gap-4 items-stretch">
              <MetricCard
                title={t("dashboard.metrics.topicInsightsCaptured.title")}
                tooltip={t("dashboard.metrics.topicInsightsCaptured.tooltip")}
                currentValue={String(vocMetrics.allTimeInsightsCount)}
                isPending={isPending}
              />
              {vocMetrics.allTimeAvgSentiment !== null && (
                <MetricCard
                  title={t("dashboard.metrics.avgSentiment.title")}
                  tooltip={t("dashboard.metrics.avgSentiment.tooltip")}
                  currentValue={vocMetrics.allTimeAvgSentiment.toFixed(1)}
                  metric={
                    <div
                      className={cn(
                        "flex flex-row items-center gap-1",
                        `text-${getSentimentColor(vocMetrics.allTimeAvgSentiment)}`,
                      )}
                    >
                      <MoveRight className="size-4" />
                      <span>{getSentimentLabel(vocMetrics.allTimeAvgSentiment, t)}</span>
                    </div>
                  }
                  isPending={isPending}
                />
              )}
            </div>
          )}

          {questionMetrics && (
            <div className="flex flex-row gap-4 items-stretch">
              <MetricCard
                title={t("dashboard.metrics.questionsCaptured.title")}
                tooltip={t("dashboard.metrics.questionsCaptured.tooltip")}
                currentValue={String(questionMetrics.allTimeCount)}
                isPending={isPending}
              />
              {questionMetrics.answeredRate !== null && (
                <MetricCard
                  title={t("dashboard.metrics.answeredRate.title")}
                  tooltip={t("dashboard.metrics.answeredRate.tooltip")}
                  currentValue={formatPercent({ amount: questionMetrics.answeredRate, fix: 0 })}
                  metric={
                    questionMetrics.knowledgeGapCount > 0
                      ? t("dashboard.metrics.answeredRate.knowledgeGapMetric", {
                          count: questionMetrics.knowledgeGapCount,
                        })
                      : t("dashboard.metrics.answeredRate.metric", {
                          count: questionMetrics.answeredCount,
                        })
                  }
                  isPending={isPending}
                />
              )}
            </div>
          )}

          {issueMetrics && (
            <div className="flex flex-row gap-4 items-stretch">
              <MetricCard
                title={t("dashboard.metrics.issuesCaptured.title")}
                tooltip={t("dashboard.metrics.issuesCaptured.tooltip")}
                currentValue={String(issueMetrics.allTimeCount)}
                isPending={isPending}
              />
              {issueMetrics.highUrgencyRate !== null && (
                <MetricCard
                  title={t("dashboard.metrics.highUrgencyRate.title")}
                  tooltip={t("dashboard.metrics.highUrgencyRate.tooltip")}
                  currentValue={formatPercent({ amount: issueMetrics.highUrgencyRate, fix: 0 })}
                  metric={t("dashboard.metrics.highUrgencyRate.metric", {
                    count: issueMetrics.highUrgencyCount,
                  })}
                  isPending={isPending}
                />
              )}
            </div>
          )}

          {actionRequestMetrics && (
            <div className="flex flex-row gap-4 items-stretch">
              <MetricCard
                title={t("dashboard.metrics.actionRequestsCaptured.title")}
                tooltip={t("dashboard.metrics.actionRequestsCaptured.tooltip")}
                currentValue={String(actionRequestMetrics.allTimeCount)}
                isPending={isPending}
              />
              {actionRequestMetrics.fulfillmentRate !== null && (
                <MetricCard
                  title={t("dashboard.metrics.fulfillmentRate.title")}
                  tooltip={t("dashboard.metrics.fulfillmentRate.tooltip")}
                  currentValue={formatPercent({
                    amount: actionRequestMetrics.fulfillmentRate,
                    fix: 0,
                  })}
                  metric={
                    actionRequestMetrics.eligibilityGapCount > 0
                      ? t("dashboard.metrics.fulfillmentRate.eligibilityGapMetric", {
                          count: actionRequestMetrics.eligibilityGapCount,
                        })
                      : t("dashboard.metrics.fulfillmentRate.metric", {
                          count: actionRequestMetrics.fulfilledCount,
                        })
                  }
                  isPending={isPending}
                />
              )}
            </div>
          )}

          {/* Activity chart */}
          {(activityData || isPending) && <div className="min-h-[200px]">{activityChart}</div>}

          <TopicItemsTable
            domain={domain}
            canonicalTopicId={canonicalTopicId}
            filterBy={filterBy}
          />
        </div>
        {navigateTo && (
          <div className="p-4 border-t border-border shrink-0 flex justify-end">
            <Button
              variant="outline"
              size="sm"
              onClick={() => {
                onClose()
                navigate(navigateTo)
              }}
            >
              <span>{getNavigateToLabel(domain, t)}</span>
              <ArrowRight size={16} />
            </Button>
          </div>
        )}
      </SheetContent>
    </Sheet>
  )
}

function toPercent(value: number, baseValue: number) {
  if (!baseValue) return undefined
  return Math.round((value / baseValue) * 100)
}

function getNavigateToLabel(domain: ClusterDomain, t: TFunction) {
  switch (domain) {
    case ClusterDomainEnum.Support:
      return t("dashboard.tooltip.goToSupport")
    case ClusterDomainEnum.Issue:
    case ClusterDomainEnum.Question:
    case ClusterDomainEnum.ActionRequest:
      return t(`dashboard.tooltip.goToSupportByDomain.${domain}`)
    default:
      return t("dashboard.tooltip.goToConversations")
  }
}
